import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { attendanceHistoryApi } from '../api/attendanceHistoryApi';
import { appToast } from '../../../../../components/AppToast';

export function useAttendanceHistoryList() {
  const [searchParams] = useSearchParams();
  const paramEmpId = searchParams.get('empId');
  const paramEmpName = searchParams.get('empName');

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [datePresetFilter, setDatePresetFilter] = useState('Current Month');
  const [fromDate, setFromDate] = useState('2026-07-01');
  const [toDate, setToDate] = useState('2026-07-31');
  const [searchField, setSearchField] = useState('Date');
  const [searchText, setSearchText] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRecordForSwipes, setSelectedRecordForSwipes] = useState(null);
  const [isSwipesModalOpen, setIsSwipesModalOpen] = useState(false);

  const employeeInfo = useMemo(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        name: paramEmpName || stored.name || stored.userName || 'Employee',
        empId: paramEmpId || stored.employee_id || stored.empCode || stored.id || '1',
        code: stored.code || (paramEmpId ? `EMP-${paramEmpId}` : 'EMP-01'),
        department: stored.department || 'General',
      };
    } catch {
      return {
        name: paramEmpName || 'Employee',
        empId: paramEmpId || '1',
        code: paramEmpId ? `EMP-${paramEmpId}` : 'EMP-01',
        department: 'General',
      };
    }
  }, [paramEmpId, paramEmpName]);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      const res = await attendanceHistoryApi.getAttendanceHistory({
        emp_id: employeeInfo.empId,
        from_date: fromDate,
        to_date: toDate,
      });
      setRecords(res.data || []);
    } catch (err) {
      console.error('Failed to load attendance history records:', err);
      appToast.error('Failed to load attendance history.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [employeeInfo.empId, fromDate, toDate]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const datePresetOptions = [
    { value: 'Current Month', label: 'Current Month' },
    { value: 'Last Month', label: 'Last Month' },
    { value: 'This Quarter', label: 'This Quarter' },
    { value: 'Custom Range', label: 'Custom Range' },
  ];

  const searchFieldOptions = [
    { value: 'Date', label: 'Date' },
    { value: 'First Punch', label: 'First Punch Time' },
    { value: 'Last Punch', label: 'Last Punch Time' },
  ];

  const attendanceStatusOptions = [
    { value: 'All', label: 'All Statuses' },
    { value: 'P', label: 'Present (P)' },
    { value: 'HF', label: 'Half Day (HF)' },
    { value: 'A', label: 'Absent (A)' },
    { value: 'WO', label: 'Weekly Off (WO)' },
    { value: 'L', label: 'Leave (L)' },
    { value: 'HD', label: 'Holiday (HD)' },
  ];

  const tools = [
    { key: 'excel', label: 'Export to Excel', icon: 'table_view' },
    { key: 'pdf', label: 'Export to PDF', icon: 'picture_as_pdf' },
    { key: 'print', label: 'Print', icon: 'print' },
  ];

  const handleDatePresetChange = useCallback((preset) => {
    setDatePresetFilter(preset);
    if (preset === 'Current Month') {
      setFromDate('2026-07-01');
      setToDate('2026-07-31');
    } else if (preset === 'Last Month') {
      setFromDate('2026-06-01');
      setToDate('2026-06-30');
    }
  }, []);

  const filteredRecords = useMemo(() => {
    return records.filter((item) => {
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        const dStr = String(item.date || item.dateDisplay || '').toLowerCase();
        const fpStr = String(item.firstPunch || item.in_time || '').toLowerCase();
        const lpStr = String(item.lastPunch || item.out_time || '').toLowerCase();

        if (searchField === 'Date' && !dStr.includes(query)) {
          return false;
        }
        if (searchField === 'First Punch' && !fpStr.includes(query)) {
          return false;
        }
        if (searchField === 'Last Punch' && !lpStr.includes(query)) {
          return false;
        }
      }

      if (attendanceFilter !== 'All' && (item.attendance || item.status) !== attendanceFilter) {
        return false;
      }

      return true;
    });
  }, [records, searchText, searchField, attendanceFilter]);

  const totalCount = filteredRecords.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  const handleSearch = useCallback(() => {
    setPage(1);
    loadRecords();
    appToast.success('Attendance records filtered');
  }, [loadRecords]);

  const resetFilters = useCallback(() => {
    setDatePresetFilter('Current Month');
    setFromDate('2026-07-01');
    setToDate('2026-07-31');
    setSearchField('Date');
    setSearchText('');
    setAttendanceFilter('All');
    setPage(1);
  }, []);

  const handleToolSelect = useCallback((toolKey) => {
    const key = String(toolKey || '').toLowerCase();
    if (key.includes('print')) {
      window.print();
    } else if (key.includes('excel')) {
      appToast.success('Exporting Attendance History to Excel...');
    } else if (key.includes('pdf')) {
      appToast.success('Exporting Attendance History to PDF...');
    }
  }, []);

  const handleViewSwipes = useCallback((record) => {
    setSelectedRecordForSwipes(record);
    setIsSwipesModalOpen(true);
  }, []);

  const handleCloseSwipesModal = useCallback(() => {
    setIsSwipesModalOpen(false);
    setSelectedRecordForSwipes(null);
  }, []);

  return {
    state: {
      records: paginatedRecords,
      totalCount,
      loading,
      page,
      pageSize,
      totalPages,
      datePresetFilter,
      fromDate,
      toDate,
      searchField,
      searchText,
      attendanceStatusFilter: attendanceFilter,
      attendanceFilter,
      selectedRecordForSwipes,
      isSwipesModalOpen,
      datePresetOptions,
      searchFieldOptions,
      attendanceStatusOptions,
      attendanceOptions: attendanceStatusOptions,
      tools,
      employeeInfo,
    },
    actions: {
      setPage,
      setPageSize,
      setDatePresetFilter: handleDatePresetChange,
      setFromDate,
      setToDate,
      setSearchField,
      setSearchText,
      setAttendanceStatusFilter: setAttendanceFilter,
      setAttendanceFilter,
      handleSearch,
      resetFilters,
      handleToolSelect,
      handleViewSwipes,
      handleCloseSwipesModal,
      refresh: loadRecords,
    },
  };
}
